import React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatNumber } from 'js/lib/FormatUtils';
import {
  countTickFormatter,
  extractTooltipRow,
  RechartsTooltipPayload,
} from './MetricsCombinedChart';
import { MetricRow } from '../metricRows';
import styles from './MetricsCombinedChart.module.scss';

export type MetricsSeriesChartProps = {
  rows: MetricRow[];
  dataKey: 'records' | 'errors';
  label: string;
  color: string;
  ticks: string[];
  showXLabels?: boolean;
  height?: number;
};

const SeriesTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: RechartsTooltipPayload;
  label: string;
}) => {
  const row = extractTooltipRow(active, payload);
  if (!row) return null;
  const value = formatNumber(payload![0]?.value ?? 0);
  return (
    <div className={styles.tooltipContainer}>
      <span className={styles.tooltipDate}>{row.date}</span>
      <span className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>{label}</span>
        <span className={styles.tooltipValue}>{value}</span>
      </span>
    </div>
  );
};

export const MetricsSeriesChart = (props: MetricsSeriesChartProps) => {
  const { rows, dataKey, label, color, ticks, showXLabels = true, height = 150 } = props;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          ticks={ticks}
          interval="preserveStartEnd"
          tick={showXLabels}
          height={showXLabels ? undefined : 1}
        />
        <YAxis orientation="left" tickFormatter={countTickFormatter} />

        <Tooltip
          cursor={{ fill: 'var(--color-greyscale-100)' }}
          isAnimationActive={false}
          content={<SeriesTooltip label={label} />}
        />

        <Area type="monotone" dataKey={dataKey} fill={color} fillOpacity={0.15} stroke="none" />
        <Line type="monotone" dataKey={dataKey} stroke={color} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

MetricsSeriesChart.displayName = 'MetricsSeriesChart';
